import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

type Currency = 'EUR' | 'BAM' | 'RSD';

const currencySymbols: Record<Currency, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

function PDVCalculator() {
  const [amount, setAmount] = useState<string>('');
  const [pdvRate, setPdvRate] = useState<string>('25');
  const [includePDV, setIncludePDV] = useState<boolean>(false);
  const [currency, setCurrency] = useState<Currency>('EUR');
  const [results, setResults] = useState({
    baseAmount: 0,
    pdvAmount: 0,
    totalAmount: 0
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "PDV Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni PDV kalkulator za brz i precizan izračun PDV-a. Podržava različite stope PDV-a i valute.",
    "url": "https://online-kalkulator.com/pdv-kalkulator",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun PDV-a",
      "Podrška za različite stope PDV-a",
      "Viševalutni izračuni",
      "Automatsko ažuriranje rezultata",
      "Besplatno korištenje"
    ]
  };

  useEffect(() => {
    if (amount && pdvRate) {
      const numAmount = parseFloat(amount);
      const numRate = parseFloat(pdvRate) / 100;
      
      if (!isNaN(numAmount) && !isNaN(numRate)) {
        if (includePDV) {
          // Računanje od ukupnog iznosa (s PDV-om)
          const baseAmount = numAmount / (1 + numRate);
          const pdvAmount = numAmount - baseAmount;
          setResults({
            baseAmount: parseFloat(baseAmount.toFixed(2)),
            pdvAmount: parseFloat(pdvAmount.toFixed(2)),
            totalAmount: numAmount
          });
        } else {
          // Računanje od osnovnog iznosa (bez PDV-a)
          const pdvAmount = numAmount * numRate;
          const totalAmount = numAmount + pdvAmount;
          setResults({
            baseAmount: numAmount,
            pdvAmount: parseFloat(pdvAmount.toFixed(2)),
            totalAmount: parseFloat(totalAmount.toFixed(2))
          });
        }
      }
    } else {
      setResults({
        baseAmount: 0,
        pdvAmount: 0,
        totalAmount: 0
      });
    }
  }, [amount, pdvRate, includePDV]);

  const handlePdvRateChange = (value: string) => {
    // Remove any non-numeric characters except decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const parts = sanitizedValue.split('.');
    const formattedValue = parts.length > 1 
      ? `${parts[0]}.${parts.slice(1).join('')}`
      : sanitizedValue;
    
    setPdvRate(formattedValue);
  };

  return (
    <>
      <SEO 
        title="PDV Kalkulator | Izračun PDV-a Online"
        description="Besplatni PDV kalkulator za brz i precizan izračun PDV-a. Podržava različite stope PDV-a i valute. Idealan za poduzetnike i računovođe."
        canonicalUrl="/pdv-kalkulator"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">PDV Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Controls */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                      Iznos
                    </label>
                    <input
                      type="number"
                      id="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite iznos"
                    />
                  </div>
                  <div>
                    <label htmlFor="pdvRate" className="block text-sm font-medium text-gray-700 mb-1">
                      Stopa PDV-a (%)
                    </label>
                    <input
                      type="text"
                      id="pdvRate"
                      value={pdvRate}
                      onChange={(e) => handlePdvRateChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite stopu PDV-a"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includePDV}
                      onChange={(e) => setIncludePDV(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Iznos uključuje PDV</span>
                  </label>

                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Valuta:</label>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value as Currency)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="BAM">BAM (KM)</option>
                      <option value="RSD">RSD (RSD)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-3 border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Osnovica:</span>
                  <span className="text-lg font-semibold">
                    {results.baseAmount.toFixed(2)} {currencySymbols[currency]}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">PDV ({pdvRate}%):</span>
                  <span className="text-lg font-semibold">
                    {results.pdvAmount.toFixed(2)} {currencySymbols[currency]}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t pt-2">
                  <span className="text-gray-800 font-medium">Ukupno:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {results.totalAmount.toFixed(2)} {currencySymbols[currency]}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O PDV Kalkulatoru</h2>
                <p className="text-gray-600 leading-relaxed">
                  PDV kalkulator je koristan alat koji vam omogućuje jednostavno izračunavanje PDV-a i osnovice. 
                  Možete unijeti vlastitu stopu PDV-a i izabrati željenu valutu za prikaz rezultata.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite iznos koji želite izračunati</li>
                  <li>Unesite željenu stopu PDV-a u postotcima</li>
                  <li>Odaberite željenu valutu (EUR, BAM ili RSD)</li>
                  <li>Označite "Iznos uključuje PDV" ako unosite ukupan iznos s PDV-om</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi iznosi su prikazani u odabranoj valuti</li>
                  <li>Izračuni su zaokruženi na dvije decimale</li>
                  <li>Kalkulator automatski ažurira rezultate pri svakoj promjeni</li>
                  <li>Možete unijeti bilo koju stopu PDV-a</li>
                  <li>Podržane valute: Euro (€), Konvertibilna marka (KM), Srpski dinar (RSD)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Povezani Kalkulatori</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link 
                    to="/"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <h3 className="font-medium text-gray-800 mb-1">Osnovni Kalkulator</h3>
                    <p className="text-sm text-gray-600">Za osnovne matematičke operacije</p>
                  </Link>
                  <Link 
                    to="/kalkulator-place"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <h3 className="font-medium text-gray-800 mb-1">Kalkulator Plaće</h3>
                    <p className="text-sm text-gray-600">Izračunajte neto i bruto plaću</p>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PDVCalculator;